import {
  faUpload,
  faFileAlt,
  faEye,
  faPenAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { TInviteRequest } from '@gen2/api/invite-requests/api';
import { TInviteRequestGroup } from '@gen2/api/invite-requests/api';
import { TTemplate } from '@gen2/api/templates/api';
import {
  useGetTemplateRequestsQuery,
  useGetTemplateRequestGroupsQuery,
} from '@gen2/api/templates/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMergeTemplateModalStore } from '../store';
import {
  CardContentContainer,
  CardContentRequestContainer,
  CardContentRequestHeader,
  CardContentRequestMessage,
  HeaderText,
  StyledCheckbox,
  TemplateCardContainer,
  TemplateCardHeader,
  GroupSection,
  GroupTitle,
  RequestContainer,
  IconContainer,
  RequestIcon,
  RequestTitle,
} from './template-card.styled';

interface TemplateCardProps {
  template: TTemplate;
}

interface RequestItemProps {
  request: TInviteRequest;
  selected: boolean;
  onChange: (id: string) => void;
  getIcon: (type: string) => any;
}

export const RequestItem = ({
  request,
  selected,
  onChange,
  getIcon,
}: RequestItemProps) => (
  <RequestContainer key={request.id} data-cy={`template-request-${request.id}`}>
    <IconContainer>
      <RequestIcon icon={getIcon(request.type)} />
    </IconContainer>
    <RequestTitle>{request.title}</RequestTitle>
    <StyledCheckbox
      color="secondary"
      checked={selected}
      onClick={() => onChange(request.id)}
    />
  </RequestContainer>
);

export const TemplateCard = ({ template }: TemplateCardProps) => {
  const { t } = useTranslation('templates');
  const initialized = useRef(false);
  const {
    selectedRequests,
    setAllRequestIds,
    setSelectedRequestIds,
    setPerTemplateSelectedCount,
    appendSelectedRequestIds,
  } = useMergeTemplateModalStore();

  const templateId = template.id;

  const { data: initialTemplateRequests } =
    useGetTemplateRequestsQuery(templateId);

  const { data: templateRequestGroups } =
    useGetTemplateRequestGroupsQuery(templateId);

  const templateRequests = useMemo(
    () => initialTemplateRequests?.filter((r) => !r.request_group_id) || [],
    [initialTemplateRequests],
  );

  const groupRequestIds = useMemo(
    () =>
      templateRequestGroups?.flatMap((group: TInviteRequestGroup) =>
        group.requests.map((r: TInviteRequest) => r.id),
      ) || [],
    [templateRequestGroups],
  );

  const allRequestIds = useMemo(
    () => [...templateRequests.map((r) => r.id), ...groupRequestIds],
    [templateRequests, groupRequestIds],
  );

  const selectedCount = (selectedRequests || []).filter((id) =>
    allRequestIds.includes(id),
  ).length;

  const [templateRequestIds, setTemplateRequestIds] = useState<string[]>([]);

  useEffect(() => {
    if (
      initialTemplateRequests &&
      templateRequestGroups &&
      !initialized.current
    ) {
      setAllRequestIds({ [templateId]: allRequestIds });

      const templateRequestIdsResponse = templateRequests.map(
        (request: TInviteRequest) => request.id,
      );

      const allIds = allRequestIds;

      const noneSelected = allIds.every(
        (id) => !(selectedRequests || []).includes(id),
      );
      if (noneSelected && allIds.length > 0) {
        setTemplateRequestIds(allIds);
        appendSelectedRequestIds(allIds);
        setPerTemplateSelectedCount({
          [templateId]: allIds.length,
        });
        initialized.current = true;
        return;
      }

      if (
        selectedRequests?.some((requestId) =>
          templateRequestIdsResponse.includes(requestId),
        )
      ) {
        const filteredTemplateRequestIds =
          templateRequestIdsResponse?.filter((id) =>
            selectedRequests.includes(id),
          ) || [];

        setTemplateRequestIds(filteredTemplateRequestIds);

        return;
      }

      if (
        templateRequestIdsResponse.length < 1 &&
        (selectedRequests || []).length > 0
      ) {
        return;
      }

      if ((selectedRequests || []).length === 0) {
        setTemplateRequestIds(templateRequestIdsResponse);
        appendSelectedRequestIds(allIds);
        setPerTemplateSelectedCount({
          [templateId]: allIds.length,
        });
        initialized.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialTemplateRequests,
    templateRequestGroups,
    templateRequests,
    groupRequestIds,
    allRequestIds,
  ]);

  const handleCheckboxChange = (requestId: string) => {
    if (selectedRequests?.includes(requestId)) {
      const filteredSelectedRequestIds = selectedRequests.filter(
        (id) => id !== requestId,
      );
      const filteredTemplateRequestIds =
        templateRequestIds?.filter((id) => id !== requestId) || [];

      setTemplateRequestIds(filteredTemplateRequestIds);
      setSelectedRequestIds(filteredSelectedRequestIds);
      setPerTemplateSelectedCount({
        [templateId]: filteredTemplateRequestIds.length,
      });
    } else {
      setTemplateRequestIds((prev) => [...prev, requestId]);
      appendSelectedRequestIds([requestId]);
      setPerTemplateSelectedCount({
        [templateId]: templateRequestIds.length + 1,
      });
    }
  };

  const handleHeaderCheckboxChange = () => {
    if (selectedCount === allRequestIds.length) {
      const filteredSelectedRequests = (selectedRequests || []).filter(
        (req) => !allRequestIds.includes(req),
      );
      setTemplateRequestIds([]);
      setSelectedRequestIds(filteredSelectedRequests);
      setPerTemplateSelectedCount({ [templateId]: 0 });
    } else {
      setTemplateRequestIds(templateRequests.map((r) => r.id));
      appendSelectedRequestIds(allRequestIds);
      setPerTemplateSelectedCount({ [templateId]: allRequestIds.length });
    }
  };

  const handleRequestIcons = useCallback((type: string) => {
    switch (type) {
      case 'file-upload':
        return faUpload;
      case 'written-response':
        return faFileAlt;
      case 'read-only':
        return faEye;
      default:
        return faPenAlt;
    }
  }, []);

  if (!template) {
    return null;
  }

  return (
    <TemplateCardContainer>
      <TemplateCardHeader>
        <HeaderText>{template.name}</HeaderText>
      </TemplateCardHeader>

      <CardContentContainer>
        <p>{t('mergeModal.selectRequests')}</p>
        <CardContentRequestContainer>
          <CardContentRequestHeader>
            <CardContentRequestMessage>
              {t('mergeModal.requestColumn')}
            </CardContentRequestMessage>

            <StyledCheckbox
              color="secondary"
              indeterminate={
                selectedCount > 0 && selectedCount < allRequestIds.length
              }
              checked={
                allRequestIds.length > 0 &&
                selectedCount === allRequestIds.length
              }
              onClick={handleHeaderCheckboxChange}
            />
          </CardContentRequestHeader>
          {templateRequestGroups &&
            templateRequestGroups.length > 0 &&
            templateRequestGroups?.map((group: TInviteRequestGroup) => (
              <GroupSection key={group.id}>
                <GroupTitle>{group.name}</GroupTitle>
                {group.requests.map((request, index) => (
                  <RequestItem
                    request={request}
                    key={index}
                    selected={selectedRequests?.includes(request.id) ?? false}
                    onChange={handleCheckboxChange}
                    getIcon={handleRequestIcons}
                  />
                ))}
              </GroupSection>
            ))}
          {templateRequests?.map((request, index) => (
            <RequestItem
              request={request}
              key={index}
              selected={selectedRequests?.includes(request.id) ?? false}
              onChange={handleCheckboxChange}
              getIcon={handleRequestIcons}
            />
          ))}
        </CardContentRequestContainer>
      </CardContentContainer>
    </TemplateCardContainer>
  );
};
